/* eslint-disable complexity */
import React, { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box';

import { ListingPageHeader } from '@futrli/ui-components/components/listing-page/listing-page-header/listing-page-header';
import { ListingPageTabs } from '@futrli/ui-components/components/listing-page/listing-page-tabs/listing-page-tabs';
import { PageWidthWrapper } from '@futrli/ui-components/components/page-width-wrapper/page-width-wrapper';
import { SettingsSubNav } from '@futrli/predict/settings/components/settings-sub-nav/settings-sub-nav';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { useNavHeight } from '@futrli/predict/hooks/useNavHeight';
import { WorkspaceListView } from '@futrli/views/views/workspaces/WorkspaceListView/WorkspaceListView';

import { ConsolidationsTabContainer } from './consolidations-tab-container/consolidations-tab-container';
import { CreateOrgConPopover } from '../../components/create-org-con-popover/create-org-con-popover';
import { OrganisationsTabContainer } from './organisations-tab-container/organisations-tab-container';
import { useWorkspaceName } from '../../hooks/use-workspace-name/use-workspace-name';
import { WorkspacesActionsProvider } from '../../context/workspaces-actions-context/workspaces-actions-context';
import { WorkspaceSettingsButton } from '../../components/workspace-settings-button/workspace-settings-button';
import { getWorkspaceListingUrl } from '../../../utils/route-utils';

export enum WorkspaceTabId {
  ORGANISATIONS = 'organisations',
  CONSOLIDATIONS = 'consolidations'
}

export const WorkspaceListContainer = () => {
  const flags = useFlags();
  const intl = useIntl();
  const { navHeight } = useNavHeight();
  const { workspaceId = '' } = useParams();

  const view = useApiView(useMemo(() => new WorkspaceListView(flags, workspaceId), [flags, workspaceId]));
  const { hasMembersPermission, integrations = [], workspace, workspaces } = view.state;
  const workspaceName = useWorkspaceName(workspace);
  const hasWorkspaceActions = Object.values(workspace?.actions || {}).some(v => v);

  const tabs = useMemo(() => {
    return [
      {
        id: WorkspaceTabId.ORGANISATIONS,
        title: intl.formatMessage({
          id: 'workspaces.data_connected_organisations',
          defaultMessage: 'Data connected organisations'
        }),
        component: <OrganisationsTabContainer />
      },
      {
        id: WorkspaceTabId.CONSOLIDATIONS,
        title: intl.formatMessage({
          id: 'common.consolidations',
          defaultMessage: 'Consolidations'
        }),
        component: <ConsolidationsTabContainer />
      }
    ];
  }, [intl]);

  return (
    <>
      <SettingsSubNav
        workspaceId={workspaceId}
        onlyExternalOrgs={workspace?.flags.isPureSFA || false}
        workspacesCount={workspaces?.length || 0}
        hasUsersPermission={hasMembersPermission}
      />

      <PageWidthWrapper>
        <WorkspacesActionsProvider>
          <Box margin="auto" padding="32px 0">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <ListingPageHeader
                title={
                  <>
                    {workspaceName}
                    {workspace && hasWorkspaceActions && <WorkspaceSettingsButton workspace={workspace} />}
                  </>
                }
              />

              {!workspace?.flags?.isWorkspaceGuest && (
                <CreateOrgConPopover integrations={integrations} workspaceId={workspaceId} />
              )}
            </Box>
            <Box mt={3}>
              <ListingPageTabs
                stickyTop={navHeight}
                tabs={tabs}
                basePath={getWorkspaceListingUrl(workspaceId)}
                mountActiveTabOnly
              />
            </Box>
          </Box>
        </WorkspacesActionsProvider>
      </PageWidthWrapper>
    </>
  );
};
